import { useEffect, useMemo, useState } from 'react'
import KitchenLoader from './KitchenLoader.jsx'
import { useCateringProposal } from '../hooks/useCateringProposal.js'
import { formatEventTime } from '../lib/format.js'

// CateringProposalSection — drops into the catering inquiry detail
// surface for status='quoted' inquiries. Turns the persisted INTERNAL
// quote (catering_inquiries.quote_payload) into a warm, CLIENT-FACING
// proposal the operator reviews, edits, and copies to send.
//
// CLAUDE.md: never auto-send customer-facing emails. Copy-to-clipboard
// only — no Send button. No AI-cost language. KitchenLoader for the AI
// state. Sentence case, no emoji, no "indie".
//
// CLIENT-SAFETY: the menu quantities + total price are rendered
// DETERMINISTICALLY from the edge fn's payload (which sources them from
// quote_payload, NOT the model) so they reconcile exactly. The prose
// (intro / what's included / terms / closing) is the only model output,
// and the edge fn already strips any leaked cost signal. There is NO
// internal cost data (food cost %, per-item cost, margin, prep hours) in
// this surface by construction.
//
// State machine (single render path at the bottom, no early returns):
//   idle       — no cache row within 24h; show Draft prompt
//   generating — edge fn in flight; show KitchenLoader
//   cached     — payload from the 24h cache window; show proposal
//   ready      — fresh generate() succeeded; show proposal
//   empty      — payload._empty (tiny-data nudge OR timeout retryable)
//   error      — calm deterministic fallback per CLAUDE.md
//
// Hook ordering rule (CLAUDE.md error #310): all hooks fire BEFORE any
// conditional return. Render branches are inline-conditional below.

const LOADER_MESSAGES = [
  'Reading the quote…',
  'Writing a warm opener…',
  'Drafting the proposal…',
]

function formatCooldown(ms) {
  const s = Math.ceil(ms / 1000)
  return `Wait ${s}s`
}

function timeAgo(iso) {
  if (!iso) return ''
  const ms = Date.now() - new Date(iso).getTime()
  if (!Number.isFinite(ms) || ms < 0) return ''
  const minutes = Math.floor(ms / 60_000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes} min ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} hr ago`
  const days = Math.floor(hours / 24)
  return `${days} day${days === 1 ? '' : 's'} ago`
}

// Money formatting — these numbers come from quote_payload via the edge
// fn, never from the model. price is the customer total; per-guest is
// derived (total / headcount) for display only.
function formatMoney(n) {
  const v = Number(n || 0)
  if (!Number.isFinite(v)) return '$0'
  return v.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 })
}

function formatEventDate(iso) {
  if (!iso) return ''
  const d = new Date(iso + 'T00:00:00')
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
}

// Build a clean, email-ready plain-text proposal from the operator's
// (possibly edited) prose + the deterministic numbers. No costs other
// than the customer total + per-guest derived from it.
function buildPlainText({ event, menu, customerPrice, intro, whatsIncluded, terms, closing }) {
  const lines = []
  if (intro) { lines.push(intro.trim()); lines.push('') }

  lines.push('EVENT DETAILS')
  if (event?.event_type) lines.push(`Event: ${event.event_type}`)
  if (event?.event_date) {
    const t = formatEventTime(event?.event_time)
    lines.push(`Date: ${formatEventDate(event.event_date)}${t ? ` at ${t}` : ''}`)
  }
  if (event?.headcount) lines.push(`Guests: ${event.headcount}`)
  if (event?.location) lines.push(`Location: ${event.location}`)
  lines.push('')

  if (menu.length) {
    lines.push('MENU')
    menu.forEach((m) => {
      lines.push(`- ${m.name}${m.quantity ? ` (${m.quantity})` : ''}`)
    })
    lines.push('')
  }

  lines.push('INVESTMENT')
  lines.push(`Total: ${formatMoney(customerPrice)}`)
  const hc = Number(event?.headcount || 0)
  if (hc > 0 && customerPrice > 0) {
    lines.push(`Approximately ${formatMoney(customerPrice / hc)} per guest`)
  }
  lines.push('')

  const inc = whatsIncluded.filter((s) => s.trim())
  if (inc.length) {
    lines.push("WHAT'S INCLUDED")
    inc.forEach((s) => lines.push(`- ${s.trim()}`))
    lines.push('')
  }

  const t = terms.filter((s) => s.trim())
  if (t.length) {
    lines.push('TERMS')
    t.forEach((s) => lines.push(`- ${s.trim()}`))
    lines.push('')
  }

  if (closing) lines.push(closing.trim())

  return lines.join('\n').replace(/\n{3,}/g, '\n\n').trim()
}

export default function CateringProposalSection({ inquiry }) {
  // HOOKS FIRST (CLAUDE.md error #310). No conditional returns above this block.
  const inquiryId = inquiry?.id || null
  // Scope the ai_briefs cache read to the inquiry's OWN unit (not the
  // operator's first unit) so multi-unit operators hit the same cache row
  // the edge fn writes. See useCateringProposal.
  const { phase, payload, generatedAt, cooldownRemainingMs, generate, error } =
    useCateringProposal(inquiryId, inquiry?.restaurant_id || null)

  const [copied, setCopied] = useState(false)

  // Operator-editable prose. Seeded from the payload; the operator can
  // tweak before copying. The menu + price stay read-only (they must
  // reconcile to the quote).
  const [intro, setIntro] = useState('')
  const [whatsIncludedText, setWhatsIncludedText] = useState('')
  const [termsText, setTermsText] = useState('')
  const [closing, setClosing] = useState('')

  const hasProposal = (phase === 'ready' || phase === 'cached') &&
    payload && !payload._empty &&
    !!(payload.intro || (payload.whats_included || []).length || (payload.terms || []).length)

  // Seed the editable fields whenever a fresh/cached proposal arrives.
  // whats_included / terms are arrays in the payload; we render them as
  // newline-separated text in the editable textareas, one item per line.
  useEffect(() => {
    if (!hasProposal) return
    setIntro(payload.intro || '')
    setWhatsIncludedText((payload.whats_included || []).join('\n'))
    setTermsText((payload.terms || []).join('\n'))
    setClosing(payload.closing || '')
  }, [hasProposal, payload])

  // Deterministic numbers from the edge fn payload (sourced from
  // quote_payload, NOT the model). event/menu/price all come from here.
  const event = payload?.event || null
  const menu = useMemo(() => (Array.isArray(payload?.menu) ? payload.menu : []), [payload])
  const customerPrice = Number(payload?.customer_price || 0)
  const headcount = Number(event?.headcount || 0)
  const perGuest = headcount > 0 && customerPrice > 0 ? customerPrice / headcount : null

  // Operator-edited arrays, split back out of the textareas (one per line).
  const whatsIncluded = useMemo(
    () => whatsIncludedText.split('\n').map((s) => s.trim()).filter(Boolean),
    [whatsIncludedText],
  )
  const terms = useMemo(
    () => termsText.split('\n').map((s) => s.trim()).filter(Boolean),
    [termsText],
  )

  async function handleCopy() {
    const text = buildPlainText({
      event, menu, customerPrice, intro, whatsIncluded, terms, closing,
    })
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      // Reset the copied indicator. (Not a modal submit-state, so no
      // finally needed — but kept symmetric and self-clearing.)
      setTimeout(() => setCopied(false), 1500)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[CateringProposalSection] clipboard write failed', e)
    }
  }

  const cooldownActive = cooldownRemainingMs > 0
  const generating = phase === 'generating'
  const buttonDisabled = generating || cooldownActive
  const buttonLabel = generating
    ? 'Drafting…'
    : cooldownActive
      ? formatCooldown(cooldownRemainingMs)
      : phase === 'cached' || phase === 'ready' || phase === 'empty'
        ? 'Re-draft'
        : 'Draft proposal'

  const showIdle  = phase === 'idle'
  const showEmpty = phase === 'empty'
  const showError = phase === 'error'

  // SINGLE render path. Branches are inline-conditional, not early returns.
  return (
    <section className="mt-4 rounded-2xl border border-black/10 bg-white p-4 shadow-sm">
      <div className="flex items-baseline justify-between gap-2">
        <div>
          <div className="text-[11px] font-medium text-black/50">
            Client proposal
          </div>
          <h3 className="mt-0.5 text-sm font-semibold text-black/85">
            Draft proposal
          </h3>
          <p className="mt-0.5 text-xs text-black/55">
            Turn your saved quote into a polished proposal to send the client. Edit, then copy.
          </p>
        </div>
        <button
          type="button"
          onClick={() => generate(false)}
          disabled={buttonDisabled}
          className={[
            'rounded-md px-2.5 py-1 text-xs font-medium',
            buttonDisabled
              ? 'cursor-not-allowed bg-black/5 text-black/40'
              : 'bg-black text-white hover:bg-black/85',
          ].join(' ')}
        >
          {buttonLabel}
        </button>
      </div>

      {/* IDLE — prompt the user. */}
      {showIdle && (
        <p className="mt-3 text-sm text-black/65">
          Tap Draft proposal to turn your saved quote into a warm, client-ready proposal.
          You review and edit it, then copy to your email tool — you stay in control of when it goes out.
        </p>
      )}

      {/* GENERATING — KitchenLoader (never the generic blue spinner). */}
      {generating && (
        <div className="mt-3">
          <KitchenLoader
            messages={LOADER_MESSAGES}
            intervalMs={1800}
            size="md"
            align="col"
          />
        </div>
      )}

      {/* EMPTY — tiny-data nudge OR retryable timeout payload. */}
      {showEmpty && (
        <div className="mt-3 rounded-xl bg-black/5 p-3 text-sm text-black/70">
          {payload?.headline || "We couldn't draft a proposal right now."}
          {payload?.narrative && (
            <div className="mt-1 text-xs text-black/55">{payload.narrative}</div>
          )}
        </div>
      )}

      {/* ERROR — calm fallback, never raw error text. */}
      {showError && (
        <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50/60 p-3 text-sm text-rose-800">
          {error || "We couldn't draft a proposal right now."}
          <div className="mt-1 text-xs text-rose-700/80">
            Tap Re-draft in a moment to try again.
          </div>
        </div>
      )}

      {/* READY / CACHED — branded client-facing proposal preview. */}
      {hasProposal && (
        <div className="mt-3 space-y-3">
          {/* Intro — editable. */}
          <div>
            <label className="text-[11px] font-medium uppercase tracking-wide text-black/50">
              Opening
            </label>
            <textarea
              value={intro}
              onChange={(e) => setIntro(e.target.value)}
              rows={2}
              className="mt-1 w-full resize-y rounded-lg border border-black/10 bg-white p-2 text-sm text-black/85 focus:border-black/30 focus:outline-none"
            />
          </div>

          {/* Event summary — read-only, from the quote/inquiry. */}
          <div className="rounded-xl border border-black/10 bg-black/[0.02] p-3">
            <div className="text-[11px] font-medium uppercase tracking-wide text-black/50">
              Event details
            </div>
            <dl className="mt-1 grid grid-cols-1 gap-0.5 text-sm text-black/80">
              {event?.event_type && <div><span className="text-black/50">Event: </span>{event.event_type}</div>}
              {event?.event_date && (
                <div>
                  <span className="text-black/50">Date: </span>
                  {formatEventDate(event.event_date)}{formatEventTime(event?.event_time) ? ` at ${formatEventTime(event.event_time)}` : ''}
                </div>
              )}
              {headcount > 0 && <div><span className="text-black/50">Guests: </span>{headcount}</div>}
              {event?.location && <div><span className="text-black/50">Location: </span>{event.location}</div>}
            </dl>
          </div>

          {/* Menu — read-only, rendered from quote_payload (name + quantity
              only). NO costs. Reconciles to the quote exactly. */}
          {menu.length > 0 && (
            <div className="rounded-xl border border-black/10 bg-white p-3">
              <div className="text-[11px] font-medium uppercase tracking-wide text-black/50">
                Menu
              </div>
              <ul className="mt-1 space-y-0.5 text-sm text-black/85">
                {menu.map((m, i) => (
                  <li key={i} className="flex justify-between gap-3">
                    <span>{m.name}</span>
                    {m.quantity && <span className="shrink-0 text-black/55">{m.quantity}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Investment — the customer total from quote_payload, plus a
              derived per-guest figure. NO internal cost data. */}
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-3">
            <div className="text-[11px] font-medium uppercase tracking-wide text-emerald-800/70">
              Investment
            </div>
            <div className="mt-1 text-lg font-semibold text-emerald-900">
              {formatMoney(customerPrice)}
            </div>
            {perGuest !== null && (
              <div className="text-xs text-emerald-800/80">
                approximately {formatMoney(perGuest)} per guest
              </div>
            )}
          </div>

          {/* What's included — editable, one item per line. */}
          <div>
            <label className="text-[11px] font-medium uppercase tracking-wide text-black/50">
              What's included <span className="normal-case text-black/35">(one per line)</span>
            </label>
            <textarea
              value={whatsIncludedText}
              onChange={(e) => setWhatsIncludedText(e.target.value)}
              rows={Math.max(3, whatsIncluded.length + 1)}
              className="mt-1 w-full resize-y rounded-lg border border-black/10 bg-white p-2 text-sm text-black/85 focus:border-black/30 focus:outline-none"
            />
          </div>

          {/* Terms — editable, one item per line. */}
          <div>
            <label className="text-[11px] font-medium uppercase tracking-wide text-black/50">
              Terms <span className="normal-case text-black/35">(one per line)</span>
            </label>
            <textarea
              value={termsText}
              onChange={(e) => setTermsText(e.target.value)}
              rows={Math.max(3, terms.length + 1)}
              className="mt-1 w-full resize-y rounded-lg border border-black/10 bg-white p-2 text-sm text-black/85 focus:border-black/30 focus:outline-none"
            />
          </div>

          {/* Closing — editable. */}
          <div>
            <label className="text-[11px] font-medium uppercase tracking-wide text-black/50">
              Closing
            </label>
            <textarea
              value={closing}
              onChange={(e) => setClosing(e.target.value)}
              rows={2}
              className="mt-1 w-full resize-y rounded-lg border border-black/10 bg-white p-2 text-sm text-black/85 focus:border-black/30 focus:outline-none"
            />
          </div>

          {/* Copy — clipboard only. NO Send button (CLAUDE.md). */}
          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={handleCopy}
              className="rounded-lg bg-black px-3 py-2 text-sm font-medium text-white hover:bg-black/85"
            >
              {copied ? 'Copied' : 'Copy proposal'}
            </button>
            {generatedAt && (
              <div className="text-[10px] text-black/40">
                Generated {timeAgo(generatedAt)}
              </div>
            )}
          </div>
          <p className="text-[10px] text-black/40">
            Copies a clean, email-ready proposal. You send it from your own email — nothing goes out automatically.
          </p>
        </div>
      )}
    </section>
  )
}
